import { RadioGroup } from 'components/RadioGroup'
import { ChipRadioButton } from 'components/RadioGroup/ChipRadioButton'
import { RadioGroupProps } from 'components/RadioGroup/Types'
import { useField, useFormikContext } from 'formik'
import { BrandPlanTactic, CrosspostFrequency } from 'types'
import { useCallback } from 'react'
import { FormValues } from 'screens/Campaign/Overview/EditChannelsModal/Types'

interface Props extends Omit<RadioGroupProps, 'children' | 'value' | 'onChange'> {
  value: CrosspostFrequency
  onChange: (value: CrosspostFrequency) => void
}

export const CrosspostFrequencyRadioGroup = ({ value, onChange, ...props }: Props) => {
  return (
    <RadioGroup
      value={value}
      onChange={(value) => {
        value && onChange?.(value as CrosspostFrequency)
      }}
      {...props}
    >
      <ChipRadioButton value="always" variant="flat" size="lg">
        Yes
      </ChipRadioButton>
      <ChipRadioButton value="never" variant="flat" size="lg">
        No, I want individual posts
      </ChipRadioButton>
    </RadioGroup>
  )
}

const contentFields: (keyof Pick<
  FormValues,
  | 'instagram_contents'
  | 'facebook_contents'
  | 'linkedin_contents'
  | 'twitter_contents'
  | 'tiktok_contents'
  | 'youtube_contents'
>)[] = [
  'instagram_contents',
  'facebook_contents',
  'linkedin_contents',
  'twitter_contents',
  'tiktok_contents',
  'youtube_contents',
]

const crosspostFields: (keyof Pick<
  FormValues,
  'instagram_contents' | 'facebook_contents' | 'linkedin_contents' | 'twitter_contents' | 'youtube_contents'
>)[] = ['instagram_contents', 'facebook_contents', 'linkedin_contents', 'twitter_contents']

const videoCrosspostFields: (keyof Pick<FormValues, 'instagram_contents' | 'tiktok_contents' | 'youtube_contents'>)[] =
  ['instagram_contents', 'tiktok_contents', 'youtube_contents']

const CrosspostTacticsContentMap: Record<string, BrandPlanTactic> = {
  instagram_contents: BrandPlanTactic.Instagram,
  facebook_contents: BrandPlanTactic.Facebook,
  twitter_contents: BrandPlanTactic.Twitter,
  linkedin_contents: BrandPlanTactic.LinkedIn,
}

const VideoCrosspostTacticsMap: Record<string, BrandPlanTactic> = {
  instagram_contents: BrandPlanTactic.InstagramReel,
  youtube_contents: BrandPlanTactic.YouTube,
  tiktok_contents: BrandPlanTactic.TikTok,
}

export const FormikCrosspostFrequencyRadioGroup = (
  props: Omit<RadioGroupProps, 'value' | 'onChange'> & { name: string },
) => {
  const [field, , { setValue }] = useField<CrosspostFrequency>(props.name)
  const { values, setFieldValue } = useFormikContext<FormValues>()

  const handleChange = useCallback(
    (value: CrosspostFrequency) => {
      const tactics = values['tactics']
      const crosspostTactics = Object.fromEntries(
        Object.entries(CrosspostTacticsContentMap).filter(([_, value]) => tactics.includes(value)),
      )
      const videoCrosspostTactics = Object.fromEntries(
        Object.entries(VideoCrosspostTacticsMap).filter(([_, value]) => tactics.includes(value)),
      )
      let maxCrosspostContentsCount = 0
      let maxVideoCrosspostContentsCount = 0

      crosspostFields.forEach((field) => {
        const arr = values[field] ?? []
        arr.forEach((item) => {
          if (!['story', 'reel'].includes(item.type) && (item.count ?? 0) > maxCrosspostContentsCount) {
            maxCrosspostContentsCount = item.count ?? 0
          }
        })
      })

      videoCrosspostFields.forEach((field) => {
        const arr = values[field] ?? []
        arr.forEach((item) => {
          if (!['post', 'story'].includes(item.type) && (item.count ?? 0) > maxVideoCrosspostContentsCount) {
            maxVideoCrosspostContentsCount = item.count ?? 0
          }
        })
      })

      if (value === 'always') {
        let newTactics = new Set<BrandPlanTactic>()
        contentFields.forEach((field) => {
          setFieldValue(
            field,
            values[field].map((contentCount) => {
              if (
                Object.keys(CrosspostTacticsContentMap).includes(field) &&
                contentCount.count > 0 &&
                contentCount.type !== 'story'
              ) {
                newTactics.add(CrosspostTacticsContentMap[field])
              }
              if (
                Object.keys(VideoCrosspostTacticsMap).includes(field) &&
                contentCount.count > 0 &&
                !['story', 'post'].includes(contentCount.type)
              ) {
                newTactics.add(VideoCrosspostTacticsMap[field])
              }

              return {
                ...contentCount,
                count: contentCount.type === 'story' ? contentCount.count : 0,
              }
            }),
          )
        })

        setFieldValue('tactics', [...newTactics])
        setFieldValue('crosspost_contents', [{ type: 'post', count: maxCrosspostContentsCount }])
        setFieldValue('video_crosspost_contents', [{ type: 'post', count: maxVideoCrosspostContentsCount }])
      } else {
        contentFields.forEach((field) => {
          if (Object.keys(crosspostTactics).includes(field)) {
            setFieldValue(
              field,
              values[field].map((contentCount) => {
                return {
                  ...contentCount,
                  count: contentCount.type === 'reel' ? 0 : values['crosspost_contents'][0].count,
                }
              }),
            )
          }

          if (Object.keys(videoCrosspostTactics).includes(field)) {
            setFieldValue(
              field,
              values[field].map((contentCount) => {
                return {
                  ...contentCount,
                  count: values['video_crosspost_contents'][0].count,
                }
              }),
            )
          }
        })

        setFieldValue('crosspost_contents', [{ type: 'post', count: 0 }])
        setFieldValue('video_crosspost_contents', [{ type: 'post', count: 0 }])
      }

      setValue(value)
    },
    [values, setFieldValue, setValue],
  )

  return <CrosspostFrequencyRadioGroup value={field.value} onChange={handleChange} {...props} />
}
